/**
 * [공통] 유틸리티
 * @module @helpers/util
 */

import { useRoute } from 'vue-router'
import { I18nInfo } from '@/user/models'
import { getHeaderHeight, scrollTo } from '@/user/homepage-integration'

const t_common = new I18nInfo('common')

export function onresize() {
  const params = {
    browserMode: document.body.clientWidth > 768 ? 'pc' : 'mobile',
    browserWidth: document.body.clientWidth,
    browserHeight: document.body.clientHeight,
  }
  return params
}

/**
 * PC/MO 체크
 * @alias module:@helpers/util
 * @method isPC
 */
export function isPC() {
  const mode = onresize().browserMode
  return mode === 'pc'
}

/**
 * 첫글자 대문자 변경
 * @alias module:@helpers/util
 * @method changeFirstUpper
 */
export function changeFirstUpper(value) {
  const resultStr = `${value[0].toUpperCase()}${value.slice(1).toLowerCase()}`
  return resultStr
}

/**
 * 강의 날짜 기준 모집 여부 판단
 * @alias module:@helpers/util
 * @method checkAttendClosedDate
 * @param {string} finishAt 강의 종료 날짜 정보
 */
export function checkAttendClosedDate(finishAt) {
  const finishTime = new Date(finishAt).getTime()
  const todayTime = new Date().getTime()
  return finishTime < todayTime
}

/**
 * boolean 확인
 * @alias module:@helpers/util
 * @method isBoolean
 */
export function isBoolean(value) {
  return value === true || value === false || toString.call(value) === '[object Boolean]'
}

/**
 *
 * @param {string} path
 * @returns 현재 경로와 path가 일치하는지 여부
 */
export const matchCurrentPath = (path) => {
  const route = useRoute()
  const currentPath = route.path
  const matchResult = currentPath.match(new RegExp(path))
  return matchResult !== null && matchResult.length > 0
}

export const parseCurrency = (amount, enableCurrencySymbol = true) => {
  const currencySymbolSetup = { style: 'decimal' }
  const defaultSetup = {}

  return (
    Intl.NumberFormat(enableCurrencySymbol ? currencySymbolSetup : defaultSetup).format(amount) +
    t_common.translate('currency')
  )
}

/**
 * 날짜 포맷팅 함수
 * @param {string} startAt - 시작 날짜
 * @param {string} [finishAt] - 종료 날짜 (선택사항)
 * @returns {string} 포맷팅된 날짜 문자열
 * 당일 : "2024.01.15(월) 09:00 ~ 17:00"
 * 기간 : "2024.01.15(월) 09:00 ~ 01.17(수) 17:00" (종료일에서 연도 제거)
 */
export function formatDate(startAt, finishAt) {
  if (!startAt) return ''

  const start = new Date(startAt)

  // 시작일 포맷팅
  const startYear = start.getFullYear()
  const startMonth = String(start.getMonth() + 1).padStart(2, '0')
  const startDay = String(start.getDate()).padStart(2, '0')
  const startHour = String(start.getHours()).padStart(2, '0')
  const startMin = String(start.getMinutes()).padStart(2, '0')

  const weekdayNames = [
    t_common.translate('weekday.sun'),
    t_common.translate('weekday.mon'),
    t_common.translate('weekday.tue'),
    t_common.translate('weekday.wed'),
    t_common.translate('weekday.thu'),
    t_common.translate('weekday.fri'),
    t_common.translate('weekday.sat'),
  ]
  const startWeekday = weekdayNames[start.getDay()]

  const startDateStr = `${startYear}.${startMonth}.${startDay}(${startWeekday})`
  const startTimeStr = `${startHour}:${startMin}`

  if (!finishAt) {
    return `${startDateStr} ${startTimeStr}`
  }
  const finish = new Date(finishAt)

  // 종료일 포맷팅
  const finishHour = String(finish.getHours()).padStart(2, '0')
  const finishMin = String(finish.getMinutes()).padStart(2, '0')
  const endTimeStr = `${finishHour}:${finishMin}`

  const isSameDay = start.toDateString() === finish.toDateString()

  if (isSameDay) {
    return `${startDateStr} ${startTimeStr} ~ ${endTimeStr}`
  } else {
    const finishMonth = String(finish.getMonth() + 1).padStart(2, '0')
    const finishDay = String(finish.getDate()).padStart(2, '0')
    const finishWeekday = weekdayNames[finish.getDay()]
    const endDateStr = `${finishMonth}.${finishDay}(${finishWeekday})`
    return `${startDateStr} ${startTimeStr} ~ ${endDateStr} ${endTimeStr}`
  }
}

/**
 * 원화 포맷팅 함수 (천단위 콤마만)
 * @alias module:@helpers/util
 * @method formatCurrency
 * @param {number} amount - 포맷팅할 금액
 * @returns {string} 천단위 콤마가 추가된 문자열
 */
export function formatCurrency(amount) {
  return amount.toLocaleString('ko-KR')
}

/**
 * 모달 상단으로 스크롤 이동
 * @alias module:@helpers/util
 * @method navigateToModalTop
 */
export function navigateToModalTop() {
  const modalEl = document.querySelector('#modalTop')
  if (modalEl) {
    const offsetTop = modalEl.offsetTop + getHeaderHeight(isPC(), true)

    scrollTo(0, offsetTop, 'smooth')
  }
}

export default {
  onresize,
  isPC,
  changeFirstUpper,
  checkAttendClosedDate,
  isBoolean,
  matchCurrentPath,
  parseCurrency,
  formatDate,
  formatCurrency,
  navigateToModalTop,
}
